import React, { useEffect, useState, useRef } from "react";
import {
  Switch,
  TextField,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Chip,
  useMediaQuery,
} from "@material-ui/core";
import { Alert, ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useSettings from "../../hooks/useSettings";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  layout: {
    display: "flex",
    gap: theme.spacing(4),
    alignItems: "flex-start",
    [theme.breakpoints.down("sm")]: { flexDirection: "column", gap: theme.spacing(2) },
  },
  nav: {
    position: "sticky",
    top: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 190,
    // Celular: vira uma faixa de "pílulas" que rola para o lado e fica
    // presa no topo enquanto a página rola.
    [theme.breakpoints.down("sm")]: {
      top: 0,
      zIndex: 2,
      flexDirection: "row",
      gap: theme.spacing(0.75),
      overflowX: "auto",
      minWidth: 0,
      width: "100%",
      padding: theme.spacing(1, 0),
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
    },
  },
  navItem: {
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    // Sem isso os botões encolhiam no celular e o texto de um passava por
    // cima do outro.
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      minWidth: 0,
      borderRadius: 999,
      padding: theme.spacing(0.5, 1.5),
      border: `1px solid ${theme.palette.divider}`,
    },
  },
  navItemActive: {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.primary.main,
    [theme.breakpoints.down("sm")]: { borderColor: theme.palette.primary.main },
  },
  content: { flex: 1, minWidth: 0, width: "100%" },
  section: { marginBottom: theme.spacing(4) },
  sectionTitle: { fontWeight: 700, fontSize: "1rem" },
  sectionDesc: {
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
    marginBottom: theme.spacing(1),
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("xs")]: { flexWrap: "wrap" },
  },
  // Base de 140px: o interruptor continua ao lado do texto, e um controle
  // largo (seletor de opções) desce para baixo dele quando não cabe. Antes o
  // texto encolhia até virar uma palavra por linha.
  rowText: { minWidth: 0, flex: "1 1 140px" },
  rowLabel: { fontWeight: 600, fontSize: "0.92rem" },
  rowHelp: { color: theme.palette.text.secondary, fontSize: "0.8rem" },
  rowControl: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
    maxWidth: "100%",
    marginLeft: "auto",
  },
  // Seletor de opções (ToggleButtonGroup): no celular ocupa a largura toda,
  // com as opções dividindo o espaço.
  rowControlWide: {
    [theme.breakpoints.down("xs")]: {
      flex: "1 1 100%",
      "& $saved": { minWidth: 0 },
    },
  },
  segmentedGroup: {
    [theme.breakpoints.down("xs")]: {
      flex: 1,
      minWidth: 0,
      "& .MuiToggleButton-root": {
        flex: 1,
        minWidth: 0,
        padding: theme.spacing(0.75, 0.25),
        fontSize: "0.7rem",
        lineHeight: 1.25,
        textTransform: "none",
        whiteSpace: "normal",
      },
    },
  },
  saved: {
    color: theme.palette.success.main,
    fontSize: "0.75rem",
    minWidth: 40,
    textAlign: "right",
    // No celular não reserva espaço para o "Salvo": empurrava o interruptor
    // para a linha de baixo.
    [theme.breakpoints.down("xs")]: { minWidth: 0 },
  },
  card: { padding: theme.spacing(2), borderRadius: 10, marginTop: theme.spacing(1.5) },
  cardTitle: { fontWeight: 700, display: "flex", alignItems: "center", gap: theme.spacing(1) },
  preview: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.palette.action.hover,
    fontSize: "0.82rem",
    whiteSpace: "pre-wrap",
  },
  secretRow: { display: "flex", gap: theme.spacing(1), alignItems: "center" },
  mono: { fontFamily: "monospace" },
}));

const DEFAULT_FEEDBACK_MESSAGE =
  `Olá {{name}}! Seu atendimento de *{{service}}* foi concluído. Como foi a sua experiência?\n\n` +
  `*1* - _Muito insatisfeito_\n*2* - _Insatisfeito_\n*3* - _Regular_\n*4* - _Satisfeito_\n*5* - _Muito satisfeito_\n\n` +
  `Sua opinião é muito importante para nós! 🙏`;

// Mesmos textos padrão que o backend usa quando o campo está vazio.
const DEFAULT_BILLING_TEMPLATE =
  "Olá {nome}! Lembrete: {descricao} no valor de {valor} {quando}. Qualquer dúvida, estamos à disposição. 🙂";
const DEFAULT_REACTIVATION_TEMPLATE =
  "Olá {nome}! Sentimos sua falta por aqui. 😊 Podemos ajudar em alguma coisa?";


const SECTIONS = [
  { id: "opt-atendimento", label: "Atendimento" },
  { id: "opt-crm", label: "CRM" },
  { id: "opt-sistema", label: "Sistema" },
  { id: "opt-agendamentos", label: "Agendamentos" },
  { id: "opt-integracoes", label: "Integrações" },
];

// Linha de configuração: texto à esquerda, controle e "Salvo" à direita.
function SettingRow({ label, help, saved, wide, children }) {
  const classes = useStyles();
  return (
    <div className={classes.row}>
      <div className={classes.rowText}>
        <Typography className={classes.rowLabel}>{label}</Typography>
        {help && <Typography className={classes.rowHelp}>{help}</Typography>}
      </div>
      <div className={`${classes.rowControl} ${wide ? classes.rowControlWide : ""}`}>
        <span className={classes.saved} aria-live="polite">{saved ? "Salvo" : ""}</span>
        {children}
      </div>
    </div>
  );
}

// Campo de texto que salva sozinho 800 ms depois de parar de digitar.
function AutoSaveText({ value: externalValue, onSave, saving, ...props }) {
  const [value, setValue] = useState(externalValue || "");
  const timerRef = useRef(null);

  useEffect(() => {
    setValue(externalValue || "");
  }, [externalValue]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleChange = (e) => {
    const next = e.target.value;
    setValue(next);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onSave(next), 800);
  };

  return (
    <TextField
      {...props}
      value={value}
      onChange={handleChange}
      variant="outlined"
      margin="dense"
      fullWidth
      InputProps={{
        endAdornment: saving ? <CircularProgress size={16} /> : null,
      }}
    />
  );
}

// Segredo de integração: a API só devolve o final (••••1234). Para trocar, o
// usuário digita o novo valor e confirma; o valor antigo nunca aparece.
function SecretField({ label, value, onSave, saving, helperText }) {
  const classes = useStyles();
  const configured = Boolean(value);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const confirm = async () => {
    const ok = await onSave(draft.trim());
    if (ok) {
      setEditing(false);
      setDraft("");
    }
  };

  if (!editing) {
    return (
      <TextField
        label={label}
        value={configured ? value : ""}
        placeholder="Não configurado"
        variant="outlined"
        margin="dense"
        fullWidth
        helperText={helperText}
        // Rótulo sempre na borda: vazio, ele ficava dentro do campo e passava
        // por baixo do botão Informar no celular.
        InputLabelProps={{ shrink: true }}
        InputProps={{
          readOnly: true,
          classes: { input: classes.mono },
          endAdornment: (
            <Button size="small" color="primary" onClick={() => setEditing(true)} style={{ marginLeft: 4, flexShrink: 0 }}>
              {configured ? "Trocar" : "Informar"}
            </Button>
          ),
        }}
      />
    );
  }

  return (
    <Box>
      <TextField
        label={`${label} (novo valor)`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        variant="outlined"
        margin="dense"
        fullWidth
        autoFocus
        autoComplete="off"
        helperText="Cole o valor completo. O atual não é exibido por segurança."
      />
      <div className={classes.secretRow}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={saving || (!draft.trim() && !configured)}
          onClick={confirm}
        >
          {draft.trim() ? "Salvar" : "Remover chave"}
        </Button>
        <Button size="small" onClick={() => { setEditing(false); setDraft(""); }}>
          Cancelar
        </Button>
        {saving && <CircularProgress size={16} />}
      </div>
    </Box>
  );
}

export default function Options({ settings, scheduleTypeChanged, onSaved }) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { update } = useSettings();

  const [state, setState] = useState({});
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const savedTimers = useRef({});

  useEffect(() => {
    if (Array.isArray(settings)) {
      const initial = {};
      settings.forEach((s) => {
        if (s.key) initial[s.key] = s.value;
      });
      setState(initial);
      if (typeof scheduleTypeChanged === "function") {
        scheduleTypeChanged(initial.scheduleType === "company");
      }
    }
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timers = savedTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  const flashSaved = (key) => {
    setSaved((prev) => ({ ...prev, [key]: true }));
    clearTimeout(savedTimers.current[key]);
    savedTimers.current[key] = setTimeout(
      () => setSaved((prev) => ({ ...prev, [key]: false })),
      1800
    );
  };

  // Devolve true quando salvou. Se falhar, a tela continua com o valor antigo.
  const save = async (key, value) => {
      setSaving((prev) => ({ ...prev, [key]: true }));
      try {
        const result = await update({ key, value });
        // Segredos voltam mascarados; os demais, com o valor gravado.
        setState((prev) => ({ ...prev, [key]: result?.value ?? value }));
        flashSaved(key);
        // A aba é desmontada ao trocar de aba; o pai guarda o valor novo para
        // a tela não voltar ao valor antigo.
        if (typeof onSaved === "function") onSaved(key, result?.value ?? value);
        if (key === "scheduleType" && typeof scheduleTypeChanged === "function") {
          scheduleTypeChanged(value === "company");
        }
        return true;
      } catch (err) {
        toastError(err);
        return false;
      } finally {
        setSaving((prev) => ({ ...prev, [key]: false }));
      }
  };

  const get = (key, fallback = "") => (state[key] === undefined || state[key] === null ? fallback : state[key]);
  const isOn = (key, fallback = "disabled") => get(key, fallback) === "enabled";

  const toggle = ({ k, fallback, label, help }) => (
    <SettingRow label={label} help={help} saved={saved[k]}>
      <Switch
        color="primary"
        checked={isOn(k, fallback)}
        disabled={Boolean(saving[k])}
        onChange={(e) => save(k, e.target.checked ? "enabled" : "disabled")}
        inputProps={{ "aria-label": label }}
      />
    </SettingRow>
  );

  const segmented = ({ k, fallback, label, help, options }) => (
    <SettingRow label={label} help={help} saved={saved[k]} wide>
      <ToggleButtonGroup
        exclusive
        size="small"
        className={classes.segmentedGroup}
        value={get(k, fallback)}
        onChange={(_, v) => v && save(k, v)}
        aria-label={label}
      >
        {options.map((o) => (
          <ToggleButton key={o.value} value={o.value} disabled={Boolean(saving[k])}>
            {o.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </SettingRow>
  );

  const navRef = useRef(null);

  // Rola até a seção descontando a faixa de seções presa no topo (celular),
  // para o título não ficar escondido atrás dela. (scrollMarginTop não serve:
  // o JSS desta versão descarta a propriedade.)
  const findScroller = (el) => {
    let scroller = el.parentElement;
    while (scroller && scroller !== document.body) {
      const { overflowY } = window.getComputedStyle(scroller);
      if (/(auto|scroll)/.test(overflowY) && scroller.scrollHeight > scroller.clientHeight) break;
      scroller = scroller.parentElement;
    }
    return scroller;
  };

  const getOffset = () => {
    const nav = navRef.current;
    const navIsBar = nav && window.getComputedStyle(nav).flexDirection === "row";
    return (navIsBar ? nav.offsetHeight : 0) + 8;
  };

  // Durante a rolagem iniciada por clique no menu, o destaque fica na seção clicada.
  const lockUntil = useRef(0);

  const goTo = (id) => {
    setActiveSection(id);
    lockUntil.current = Date.now() + 900;
    const el = document.getElementById(id);
    if (!el) return;

    const scroller = findScroller(el);
    const offset = getOffset();

    if (!scroller || scroller === document.body) {
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
      return;
    }
    const top =
      el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - offset;
    scroller.scrollTo({ top, behavior: "smooth" });
  };

  // Ao rolar a página, destaca no menu a seção que está no topo da tela.
  useEffect(() => {
    const first = document.getElementById(SECTIONS[0].id);
    if (!first) return undefined;
    const scroller = findScroller(first);
    const target = !scroller || scroller === document.body ? window : scroller;

    const onScroll = () => {
      if (Date.now() < lockUntil.current) return;
      const isWindow = target === window;
      const baseTop = isWindow ? 0 : scroller.getBoundingClientRect().top;
      const limit = baseTop + getOffset() + 40;
      const scrollTop = isWindow ? window.scrollY : scroller.scrollTop;
      const viewH = isWindow ? window.innerHeight : scroller.clientHeight;
      const fullH = isWindow ? document.documentElement.scrollHeight : scroller.scrollHeight;

      let current = SECTIONS[0].id;
      SECTIONS.forEach((s) => {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= limit) current = s.id;
      });
      // No fim da página, as últimas seções (curtas) nunca chegam ao topo.
      if (scrollTop + viewH >= fullH - 4) current = SECTIONS[SECTIONS.length - 1].id;
      setActiveSection(current);
    };

    target.addEventListener("scroll", onScroll, { passive: true });
    return () => target.removeEventListener("scroll", onScroll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Celular: mantém a seção escolhida visível na faixa que rola para o lado
  // (mexe só no scroll horizontal da faixa, nunca no da página).
  useEffect(() => {
    const nav = navRef.current;
    const button = nav?.querySelector('[aria-current="true"]');
    if (!nav || !button || nav.scrollWidth <= nav.clientWidth) return;
    const target = button.offsetLeft - (nav.clientWidth - button.offsetWidth) / 2;
    nav.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [activeSection]);

  const billingText = get("crmBillingReminderTemplate") || DEFAULT_BILLING_TEMPLATE;
  const billingPreview = billingText
    .replace(/\{nome\}/g, "Marina")
    .replace(/\{valor\}/g, "R$ 149,90")
    .replace(/\{vencimento\}/g, "03/11/2026")
    .replace(/\{descricao\}/g, "Mensalidade de novembro")
    .replace(/\{quando\}/g, "vence em 3 dias");

  const ixcOk = Boolean(get("ipixc")) && Boolean(get("tokenixc"));
  const mkOk = Boolean(get("ipmkauth")) && Boolean(get("clientidmkauth")) && Boolean(get("clientsecretmkauth"));
  const asaasOk = Boolean(get("asaas"));

  const statusChip = (ok) => (
    <Chip
      size="small"
      label={ok ? "Configurado" : "Não configurado"}
      color={ok ? "primary" : "default"}
      variant={ok ? "default" : "outlined"}
    />
  );

  return (
    <div className={classes.layout}>
      <nav className={classes.nav} aria-label="Seções de opções" ref={navRef}>
        {SECTIONS.map((s) => (
          <Button
            key={s.id}
            size="small"
            className={`${classes.navItem} ${activeSection === s.id ? classes.navItemActive : ""}`}
            onClick={() => goTo(s.id)}
            aria-current={activeSection === s.id ? "true" : undefined}
          >
            {s.label}
          </Button>
        ))}
      </nav>

      <div className={classes.content}>
        <Alert severity="info" style={{ marginBottom: 24 }}>
          Cada opção é salva ao mudar. Aparece "Salvo" ao lado quando deu certo; se falhar, a opção volta ao valor anterior.
        </Alert>

        <section id="opt-atendimento" className={classes.section}>
          <Typography className={classes.sectionTitle}>Atendimento</Typography>
          <Typography className={classes.sectionDesc}>Como o sistema se comporta durante as conversas.</Typography>
          {toggle({ k: "userRating", label: "Avaliação do atendimento", help: "O cliente avalia ao encerrar a conversa." })}
          {toggle({ k: "sendGreetingAccepted", label: "Saudação ao aceitar", help: "Envia mensagem automática quando o atendente aceita a conversa." })}
          {toggle({ k: "sendMsgTransfTicket", label: "Avisar o cliente ao transferir", help: "Notifica o cliente quando a conversa muda de atendente." })}
          {toggle({ k: "call", label: "Aceitar chamadas de voz", help: "Define se a plataforma aceita chamadas de voz pelo WhatsApp." })}
          {toggle({ k: "CheckMsgIsGroup", label: "Ignorar mensagens de grupos", help: "Mensagens de grupos do WhatsApp não geram conversa." })}
        </section>

        <section id="opt-crm" className={classes.section}>
          <Typography className={classes.sectionTitle}>CRM</Typography>
          <Typography className={classes.sectionDesc}>Negócios, tarefas e envios automáticos do CRM.</Typography>
          {toggle({ k: "crmAutoOpportunity", fallback: "enabled", label: "Criar negócio automaticamente", help: "Conversa nova cria ou vincula um negócio no funil do CRM." })}
          {toggle({ k: "crmStageTask", fallback: "enabled", label: "Tarefa por etapa do funil", help: "O texto da tarefa é definido em cada etapa, no botão Configurar do CRM." })}
          {segmented({
            k: "crmOwnerVisibility",
            fallback: "disabled",
            label: "Quem vê os contatos",
            help: "Por carteira: o atendente vê só os seus contatos e os sem responsável. O admin vê tudo.",
            options: [
              { value: "disabled", label: "Todos veem todos" },
              { value: "enabled", label: "Por carteira" },
            ],
          })}

          <Paper variant="outlined" className={classes.card}>
            <Typography component="div" className={classes.cardTitle}>Régua de cobrança</Typography>
            {toggle({ k: "crmBillingReminder", label: "Lembrar lançamentos a receber", help: "Avisa 3 dias antes, no dia e 3 dias depois do vencimento. Roda todo dia às 09:00." })}
            {isOn("crmBillingReminder") && (
              <Box mt={1}>
                <AutoSaveText
                  label="Mensagem da cobrança"
                  value={get("crmBillingReminderTemplate")}
                  onSave={(v) => save("crmBillingReminderTemplate", v)}
                  saving={saving.crmBillingReminderTemplate}
                  placeholder={DEFAULT_BILLING_TEMPLATE}
                  multiline
                  rows={3}
                  helperText="Variáveis: {nome} {valor} {vencimento} {descricao} {quando}. Vazio usa o texto padrão."
                />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="textSecondary">
                    {saved.crmBillingReminderTemplate ? "Salvo" : ""}
                  </Typography>
                  <Button size="small" color="primary" onClick={() => save("crmBillingReminderTemplate", "")}>
                    Restaurar padrão
                  </Button>
                </Box>
                <div className={classes.preview}>
                  <strong>Prévia:</strong> {billingPreview}
                </div>
              </Box>
            )}
          </Paper>

          <Paper variant="outlined" className={classes.card}>
            <Typography component="div" className={classes.cardTitle}>Reativar contatos inativos</Typography>
            {toggle({ k: "crmReactivation", label: "Reengajar quem parou de conversar", help: "No máximo 20 contatos por dia, uma vez por período de inatividade." })}
            {isOn("crmReactivation") && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <AutoSaveText
                    label="Dias sem conversar"
                    type="number"
                    inputProps={{ min: 1, max: 3650 }}
                    value={get("crmReactivationDays")}
                    onSave={(v) => save("crmReactivationDays", String(v))}
                    saving={saving.crmReactivationDays}
                    helperText="Padrão: 90 dias."
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <AutoSaveText
                    label="Mensagem de reativação"
                    value={get("crmReactivationTemplate")}
                    onSave={(v) => save("crmReactivationTemplate", v)}
                    saving={saving.crmReactivationTemplate}
                    placeholder={DEFAULT_REACTIVATION_TEMPLATE}
                    multiline
                    rows={2}
                    helperText="Variável: {nome}. Vazio usa o texto padrão."
                  />
                </Grid>
              </Grid>
            )}
          </Paper>
        </section>

        <section id="opt-sistema" className={classes.section}>
          <Typography className={classes.sectionTitle}>Sistema</Typography>
          <Typography className={classes.sectionDesc}>Horário de atendimento da empresa.</Typography>
          {segmented({
            k: "scheduleType",
            fallback: "disabled",
            label: "Controle de expediente",
            help: 'Define quem controla o horário. Com "Por empresa", a aba Horários passa a valer.',
            options: [
              { value: "disabled", label: "Desligado" },
              { value: "queue", label: isMobile ? "Departamento" : "Por departamento" },
              { value: "company", label: isMobile ? "Empresa" : "Por empresa" },
            ],
          })}
        </section>

        <section id="opt-agendamentos" className={classes.section}>
          <Typography className={classes.sectionTitle}>Agendamentos</Typography>
          <Typography className={classes.sectionDesc}>Mensagem enviada depois do atendimento.</Typography>
          {toggle({ k: "appointmentFeedback", label: "Feedback pós-atendimento", help: "Pergunta como foi o atendimento, 30 minutos depois do fim previsto do agendamento." })}
          {isOn("appointmentFeedback") && (
            <Box>
              <AutoSaveText
                label="Mensagem de feedback"
                value={get("appointmentFeedbackMessage") || DEFAULT_FEEDBACK_MESSAGE}
                onSave={(v) => save("appointmentFeedbackMessage", v)}
                saving={saving.appointmentFeedbackMessage}
                multiline
                rows={5}
                helperText="Use {{name}} para o cliente e {{service}} para o serviço agendado."
              />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" color="textSecondary">
                  {saved.appointmentFeedbackMessage ? "Salvo" : ""}
                </Typography>
                <Button size="small" color="primary" onClick={() => save("appointmentFeedbackMessage", DEFAULT_FEEDBACK_MESSAGE)}>
                  Restaurar mensagem padrão
                </Button>
              </Box>
            </Box>
          )}
        </section>

        <section id="opt-integracoes" className={classes.section}>
          <Typography className={classes.sectionTitle}>Integrações</Typography>
          <Typography className={classes.sectionDesc}>
            Para provedores de internet e cobrança. O cliente envia o CPF/CNPJ no WhatsApp e recebe a 2ª via, o PIX e, quando a integração permite, o desbloqueio. Os campos de endereço salvam sozinhos.
          </Typography>
          <Alert severity="warning" style={{ marginBottom: 8 }}>
            As chaves ficam guardadas e não voltam para o navegador. Aparece só o final; use Trocar para informar uma nova.
          </Alert>

          <Paper variant="outlined" className={classes.card}>
            <Typography component="div" className={classes.cardTitle}>IXC Soft {statusChip(ixcOk)}</Typography>
            <Typography className={classes.rowHelp}>2ª via de boleto e desbloqueio de conexão.</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <AutoSaveText
                  label="Endereço do servidor IXC"
                  value={get("ipixc")}
                  onSave={(v) => save("ipixc", v)}
                  saving={saving.ipixc}
                  helperText="Ex.: http://ixc.suaempresa.com.br"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SecretField
                  label="Token do IXC"
                  value={get("tokenixc")}
                  onSave={(v) => save("tokenixc", v)}
                  saving={saving.tokenixc}
                  helperText="Gerado no IXC em Configurações > Integrações > WebService."
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper variant="outlined" className={classes.card}>
            <Typography component="div" className={classes.cardTitle}>MK-Auth {statusChip(mkOk)}</Typography>
            <Typography className={classes.rowHelp}>Consulta por CPF/CNPJ, boleto em PDF, PIX e desbloqueio. Tem prioridade sobre o IXC quando os dois estão configurados.</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <AutoSaveText
                  label="Endereço do servidor MK-Auth"
                  value={get("ipmkauth")}
                  onSave={(v) => save("ipmkauth", v)}
                  saving={saving.ipmkauth}
                  helperText="Ex.: http://mk.suaempresa.com.br"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <AutoSaveText
                  label="Client ID"
                  value={get("clientidmkauth")}
                  onSave={(v) => save("clientidmkauth", v)}
                  saving={saving.clientidmkauth}
                  helperText="ID de cliente OAuth2 do MK-Auth."
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <SecretField
                  label="Client Secret"
                  value={get("clientsecretmkauth")}
                  onSave={(v) => save("clientsecretmkauth", v)}
                  saving={saving.clientsecretmkauth}
                  helperText="Senha de cliente OAuth2 do MK-Auth."
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper variant="outlined" className={classes.card}>
            <Typography component="div" className={classes.cardTitle}>Asaas {statusChip(asaasOk)}</Typography>
            <Typography className={classes.rowHelp}>Busca cobranças em aberto ou vencidas e devolve valor, vencimento, código de barras e PIX.</Typography>
            <SecretField
              label="Chave de API do Asaas"
              value={get("asaas")}
              onSave={(v) => save("asaas", v)}
              saving={saving.asaas}
              helperText="No Asaas: Configurações > Integrações > Chave de API."
            />
          </Paper>
        </section>
      </div>
    </div>
  );
}
